/* eslint-disable no-underscore-dangle */
import { useGogoCssReset } from './css-reset-counter';
import { createDebug } from 'obug';
import { useSetupAICheck } from './check/setup';
import { AsyncReactGogoHost, loadReactStuff } from './runtime-loader';
import { defineComponent, onMounted, onUnmounted, provide, ref, shallowRef, computed, watchEffect } from '@vue/composition-api';
import { notifyReactRouterChanged } from './vue3router';
import { useStore } from '@/store';
import { prepareReactGogoRuntimeEnv } from './useGogoRuntimeEnv';
import { cloneDeep } from 'lodash';
import moduleRegistry from '@utils/dependencyLoader/module-registry';
import aiProjectLoader from '@/loaders/ai-project/loader';
import { hasCompleteArtifactCos, resolveArtifactCosToArtifact } from '@/utils/aiProjectInfo';
import { getTrustedParentOrigin } from './post-message-origin';
import { reportScreenshotReady } from '@utils/screenshot-ready';
const debug = createDebug('wuji-gogo:xy-client:GogoReactRuntimeWrapper');
export default defineComponent({
    name: 'GogoReactRuntimeWrapper',
    components: {
        AsyncReactGogoHost,
    },
    props: {
        projectId: {
            type: String,
            required: true,
        },
        env: {
            type: String,
            required: true,
        },
        branch: {
            type: String,
            required: true,
        },
    },
    setup(props) {
        useGogoCssReset();
        const { markReady } = useSetupAICheck();
        const loadLib = loadReactStuff();
        const initialUserData = shallowRef(null);
        const gogoRuntimeViewInstances = shallowRef([]);
        const routeViewContainer = shallowRef(null);
        const gogoHostReady = ref(false);
        provide('gogoRuntimeViewInstances', gogoRuntimeViewInstances);
        provide('routeViewContainer', routeViewContainer);
        provide('gogoHostReady', gogoHostReady);
        provide('gogoPortalHostClass', '');
        // #region prepareEnv for React
        const prepareEnv = async (userData) => {
            debug('[gogo-react-runtime-wrapper] prepareEnv', userData);
            const ans = await prepareReactGogoRuntimeEnv({
                collectionRefs: cloneDeep(userData.meta?.collectionRefs || []),
                externals: cloneDeep(userData.externals || []),
                projectId: props.projectId,
                pageId: 'ai',
            });
            return ({
                globals: ans.globals,
                customRequire: ans.customRequire,
            });
        };
        // #endregion
        // #region React Host callbacks
        function handleReactHostReady(controller) {
            console.log('GogoReactRuntimeWrapper: React host ready', controller);
            gogoHostReady.value = true;
            markReady(loadLib);
            debugBridge?.setup(controller);
        }
        function handleReactHostStatusChanged(status, exports) {
            debug('[gogo-react-runtime-wrapper] statusChanged', status);
            if (status === 'executed') {
                const pages = exports?.pageDescriptors;
                if (Array.isArray(pages)) {
                    notifyReactRouterChanged({ pages });
                }
                reportScreenshotReady();
            }
        }
        // #endregion
        // #region 调试能力
        const wujiAiPageUK = new URL(window.location.href).searchParams?.get('wujiAiPageUK');
        const isPreviewIFrame = !!wujiAiPageUK && window.location.search?.includes('iframe=1');
        const debugBridgeInstanceName = isPreviewIFrame ? 'preview' : 'window';
        const debugIsErrorReportable = (error) => {
            const messageLineCount = error.message.split('\n').length;
            if (/\bwuji_runtime\.\w+\.js\b/.test(error.stack?.split('\n')[messageLineCount] || ''))
                return false;
            return true;
        };
        const debugBridge = wujiAiPageUK ? useReactDebugBridge({
            wujiAiPageUK,
            onInitialData(e) {
                initialUserData.value = e;
            },
            onContentUpdate(e) {
                initialUserData.value = e;
            },
        }) : null;
        // #endregion
        const reactHostProps = computed(() => ({
            content: initialUserData.value,
            prepareEnv,
            onReady: handleReactHostReady,
            onHostStatusChanged: handleReactHostStatusChanged,
            routeViewContainer: routeViewContainer.value,
            debugAllowPicking: isPreviewIFrame,
            debugIsErrorReportable,
            debugBridgeInstanceName,
        }));
        async function resolveAiProjectArtifact(aiProjectInfo) {
            if (aiProjectInfo?.artifact) {
                return aiProjectInfo;
            }
            if (!hasCompleteArtifactCos(aiProjectInfo)) {
                return aiProjectInfo;
            }
            try {
                return {
                    ...aiProjectInfo,
                    artifact: await resolveArtifactCosToArtifact(aiProjectInfo.artifactCos),
                };
            }
            catch (error) {
                console.error('[xy-client] resolve ai artifactCos failed in runtime stage (react)', error);
                return await aiProjectLoader.getProjectInfo(props.projectId, props.env, props.branch, { forceFullArtifact: true });
            }
        }
        onMounted(async () => {
            const store = useStore();
            const aiProjectInfo = await store.dispatch('runtime/getAiProjectInfo', {
                projectId: props.projectId,
                env: props.env,
                branchId: props.branch,
            });
            // 预览桥接模式下由 editor 推送 artifact，跳过 COS/API 拉取；勿用 obug 的 debug 函数（恒为真）
            if (debugBridge)
                return;
            const resolvedAiProjectInfo = await resolveAiProjectArtifact(aiProjectInfo);
            if (resolvedAiProjectInfo !== aiProjectInfo) {
                store.commit('runtime/setter', { key: 'aiProjectInfo', value: resolvedAiProjectInfo });
            }
            initialUserData.value = resolvedAiProjectInfo?.artifact || null;
            console.log('[GogoReactRuntimeWrapper] initialUserData', initialUserData.value);
        });
        return {
            initialUserData,
            reactHostProps,
        };
    },
});
function useReactDebugBridge({ wujiAiPageUK, onInitialData, onContentUpdate }) {
    const isDisconnected = ref(false);
    const debugBridge = shallowRef(null);
    const isInitConnecting = ref(true);
    watchEffect((onCleanup) => {
        if (!isInitConnecting.value)
            return;
        const timer = setTimeout(() => {
            const url = new URL(window.location.href);
            if (!url.searchParams?.get('wujiAiPageUK'))
                return;
            url.searchParams.delete('wujiAiPageUK');
            window.location.href = url.toString();
        }, 500);
        onCleanup(() => clearTimeout(timer));
    });
    let debugBridgeForEditor;
    const channel = new MessageChannel();
    const port = channel.port1;
    port.onmessage = (e) => {
        if (isInitConnecting.value)
            isInitConnecting.value = false;
        if (isDisconnected.value)
            return;
        if (connectionTimeoutTimer) {
            clearTimeout(connectionTimeoutTimer);
            connectionTimeoutTimer = null;
        }
        if (e.data.type === '__preview-data__') {
            onInitialData(e.data.data);
            return;
        }
        if (e.data.type === '__debug-bridge_call_from_editor__') {
            const { method, args } = e.data;
            const replyPort = e.data.port;
            Promise.resolve()
                .then(() => debugBridgeForEditor?.[method](...args))
                .then(result => replyPort.postMessage({ result }), error => replyPort.postMessage({ error }));
            return;
        }
        if (e.data.type === '__preview-bad-request__') {
            closeDebugConnection();
            return;
        }
        if (e.data.type === '__preview-close__') {
            debugBridge.value = null;
            return;
        }
        if (e.data.type === '__editor-update-dependency__') {
            const { bundle, dependencies, port: depPort } = e.data;
            moduleRegistry.load({ bundle, dependencies })
                .then(() => depPort.postMessage({ type: '__editor-update-dependency-done__', time: Date.now() }))
                .catch(error => depPort.postMessage({ type: '__editor-update-dependency-error__', error }));
        }
    };
    port.start();
    let connectionTimeoutTimer = setTimeout(() => {
        closeDebugConnection();
    }, 3000);
    function closeDebugConnection() {
        if (isDisconnected.value)
            return;
        isDisconnected.value = true;
        debugBridge.value = null;
        if (connectionTimeoutTimer) {
            clearTimeout(connectionTimeoutTimer);
            connectionTimeoutTimer = null;
        }
        port.close();
    }
    debugBridge.value = new Proxy(Object.freeze({}), {
        get(_, action) {
            return function (...args) {
                return new Promise((resolve, reject) => {
                    const ch = new MessageChannel();
                    ch.port1.onmessage = (e) => {
                        const { result, error } = e.data;
                        if (error)
                            reject(error);
                        else
                            resolve(result);
                        ch.port1.close();
                    };
                    ch.port1.start();
                    port?.postMessage({ type: '__debug-bridge__', action, args, port: ch.port2 }, [ch.port2]);
                });
            };
        },
    });
    const sendClosed = () => port?.postMessage({ type: '__preview-close__' });
    window.addEventListener('beforeunload', sendClosed);
    onUnmounted(() => {
        sendClosed();
        window.removeEventListener('beforeunload', sendClosed);
    });
    const trustedParentOrigin = getTrustedParentOrigin();
    if (trustedParentOrigin) {
        (window.opener || window.parent || window.top)?.postMessage({
            type: '__preview-iframe-ready__',
            key: wujiAiPageUK,
            port: channel.port2,
        }, trustedParentOrigin, [channel.port2]);
    }
    function setup(controller) {
        const originalBridge = controller.getDebugBridgeForEditor();
        debugBridgeForEditor = new Proxy(originalBridge, {
            get(target, prop) {
                const original = target[prop];
                if (prop === 'editorUpdateUserCode') {
                    return (...args) => {
                        const content = args[0]?.content;
                        if (content) {
                            onContentUpdate(content);
                        }
                        return original?.apply(target, args);
                    };
                }
                return typeof original === 'function' ? original.bind(target) : original;
            },
        });
        controller.setDebugBridge(debugBridge.value);
    }
    return { setup };
}
